import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Typography, Tag, Button, Select, Space, Spin, Modal, Form, Input, InputNumber,
  message, Popconfirm, Tooltip,
} from 'antd';
import { Plus, Eye, Pencil, Trash2, GripVertical } from 'lucide-react';
import { opportunitiesApi, leadsApi, propertiesApi } from '../../services/api';

const { Title, Text } = Typography;

const STAGES = [
  'INITIAL_CONTACT', 'VISIT_SCHEDULED', 'VISIT_DONE',
  'OFFER_MADE', 'NEGOTIATION', 'DOCUMENTATION',
  'CLOSED_WON', 'CLOSED_LOST',
] as const;

const stageLabels: Record<string, string> = {
  INITIAL_CONTACT: 'Contacto inicial', VISIT_SCHEDULED: 'Visita programada',
  VISIT_DONE: 'Visita realizada', OFFER_MADE: 'Oferta enviada',
  NEGOTIATION: 'Negociación', DOCUMENTATION: 'Documentación',
  CLOSED_WON: 'Cerrada ganada', CLOSED_LOST: 'Cerrada perdida',
};

const stageColors: Record<string, string> = {
  INITIAL_CONTACT: '#1890ff', VISIT_SCHEDULED: '#13c2c2',
  VISIT_DONE: '#52c41a', OFFER_MADE: '#faad14',
  NEGOTIATION: '#fa8c16', DOCUMENTATION: '#722ed1',
  CLOSED_WON: '#389e0d', CLOSED_LOST: '#cf1322',
};

export default function OpportunitiesPage() {
  const [kanban, setKanban] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [leads, setLeads] = useState<any[]>([]);
  const [properties, setProperties] = useState<any[]>([]);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const fetchData = useCallback(() => {
    setLoading(true);
    opportunitiesApi.kanban()
      .then((r) => setKanban(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const loadFormData = async () => {
    const [l, p] = await Promise.all([
      leadsApi.list({ limit: 100 }),
      propertiesApi.list({ limit: 100 }),
    ]);
    setLeads(l.data.data);
    setProperties(p.data.data);
  };

  const openCreate = async () => {
    setEditing(null);
    form.resetFields();
    await loadFormData();
    setModalOpen(true);
  };

  const openEdit = async (opp: any) => {
    setEditing(opp);
    await loadFormData();
    form.setFieldsValue({
      title: opp.title,
      leadId: opp.leadId || opp.lead?.id,
      propertyId: opp.propertyId || opp.property?.id,
      value: opp.value,
      probability: opp.probability,
      stage: opp.stage,
      notes: opp.notes,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editing) {
        await opportunitiesApi.update(editing.id, values);
        message.success('Oportunidad actualizada');
      } else {
        await opportunitiesApi.create(values);
        message.success('Oportunidad creada');
      }
      setModalOpen(false);
      setEditing(null);
      form.resetFields();
      fetchData();
    } catch {
      message.error(editing ? 'Error al actualizar' : 'Error al crear');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await opportunitiesApi.delete(id);
      message.success('Oportunidad eliminada');
      fetchData();
    } catch { message.error('Error al eliminar'); }
  };

  /* ─── Drag & Drop ─────────────────────────────────── */
  const handleDragStart = (e: React.DragEvent, oppId: string) => {
    e.dataTransfer.setData('text/plain', oppId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggingId(oppId);
  };

  const handleDragOver = (e: React.DragEvent, stage: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stage);
  };

  const handleDragLeave = () => {
    setDragOverStage(null);
  };

  const handleDrop = async (e: React.DragEvent, targetStage: string) => {
    e.preventDefault();
    setDragOverStage(null);
    const oppId = e.dataTransfer.getData('text/plain');
    setDraggingId(null);

    if (!oppId) return;

    // Find current stage of the opp
    let currentStage = '';
    for (const col of kanban) {
      if (col.opportunities.some((o: any) => o.id === oppId)) {
        currentStage = col.stage;
        break;
      }
    }

    if (currentStage === targetStage) return;

    // Optimistic update — move card immediately in UI
    setKanban(prev => {
      const next = prev.map(col => ({
        ...col,
        opportunities: col.opportunities.filter((o: any) => o.id !== oppId),
        count: col.stage === currentStage ? col.count - 1 : col.count,
      }));

      const opp = prev.find(c => c.stage === currentStage)?.opportunities.find((o: any) => o.id === oppId);
      if (opp) {
        const targetCol = next.find(c => c.stage === targetStage);
        if (targetCol) {
          targetCol.opportunities.push({ ...opp, stage: targetStage });
          targetCol.count += 1;
        }
      }
      return next;
    });

    // API update
    try {
      await opportunitiesApi.update(oppId, { stage: targetStage });
      message.success(`Movida a ${stageLabels[targetStage]}`);
    } catch {
      message.error('Error al mover oportunidad');
      fetchData(); // Revert on error
    }
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverStage(null);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 100 }}><Spin size="large" /></div>;

  // Ensure all stages exist in kanban, even empty ones
  const kanbanMap = new Map(kanban.map(col => [col.stage, col]));
  const fullKanban = STAGES.map(stage => kanbanMap.get(stage) || { stage, count: 0, opportunities: [], totalValue: 0 });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Pipeline de Oportunidades</Title>
        <Button type="primary" icon={<Plus size={14} />} onClick={openCreate}>Nueva Oportunidad</Button>
      </div>

      {/* Kanban board */}
      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 16 }}>
        {fullKanban.map((col) => (
          <div
            key={col.stage}
            style={{ minWidth: 260, maxWidth: 300, flex: '0 0 260px' }}
            onDragOver={(e) => handleDragOver(e, col.stage)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, col.stage)}
          >
            {/* Column header */}
            <div style={{
              background: stageColors[col.stage], color: '#fff',
              padding: '8px 12px', borderRadius: '8px 8px 0 0',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <Text strong style={{ color: '#fff', fontSize: 12 }}>{stageLabels[col.stage]}</Text>
              <Tag style={{ margin: 0 }}>{col.count}</Tag>
            </div>

            {/* Column body (drop zone) */}
            <div style={{
              background: dragOverStage === col.stage ? '#e6f7ff' : '#fafafa',
              border: dragOverStage === col.stage ? '2px dashed #1890ff' : '2px solid transparent',
              padding: 8,
              borderRadius: '0 0 8px 8px',
              minHeight: 200,
              transition: 'all 0.2s ease',
            }}>
              {col.opportunities.map((opp: any) => (
                <div
                  key={opp.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, opp.id)}
                  onDragEnd={handleDragEnd}
                  style={{
                    opacity: draggingId === opp.id ? 0.4 : 1,
                    marginBottom: 8,
                    cursor: 'grab',
                    transition: 'opacity 0.2s',
                  }}
                >
                  <Card
                    size="small"
                    style={{
                      borderLeft: `3px solid ${stageColors[col.stage]}`,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    }}
                    bodyStyle={{ padding: '8px 12px' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                          <GripVertical size={12} style={{ color: '#bbb', flexShrink: 0 }} />
                          <Text strong style={{ fontSize: 13 }}>{opp.title}</Text>
                        </div>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {opp.lead?.firstName} {opp.lead?.lastName || ''} {opp.property ? `· ${opp.property.title}` : ''}
                        </Text>
                        {opp.value && (
                          <div style={{ marginTop: 4 }}>
                            <Text strong style={{ color: '#1F2F3F', fontSize: 13 }}>€{opp.value.toLocaleString()}</Text>
                            {opp.probability != null && <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>{opp.probability}%</Text>}
                          </div>
                        )}
                      </div>
                      <Space size={0} style={{ flexShrink: 0 }}>
                        <Tooltip title="Ver">
                          <Button type="text" size="small" icon={<Eye size={12} />} onClick={(e) => { e.stopPropagation(); navigate(`/opportunities/${opp.id}`); }} />
                        </Tooltip>
                        <Tooltip title="Editar">
                          <Button type="text" size="small" icon={<Pencil size={12} />} onClick={(e) => { e.stopPropagation(); openEdit(opp); }} />
                        </Tooltip>
                        <Popconfirm title="¿Eliminar?" onConfirm={() => handleDelete(opp.id)} okText="Sí" cancelText="No">
                          <Button type="text" size="small" danger icon={<Trash2 size={12} />} onClick={(e) => e.stopPropagation()} />
                        </Popconfirm>
                      </Space>
                    </div>
                  </Card>
                </div>
              ))}

              {col.opportunities.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px 16px', color: '#bbb', fontSize: 12 }}>
                  Arrastra aquí
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        title={editing ? 'Editar Oportunidad' : 'Nueva Oportunidad'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditing(null); }}
        onOk={() => form.submit()}
        okText={editing ? 'Guardar' : 'Crear'}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="title" label="Título" rules={[{ required: true }]}><Input /></Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="leadId" label="Lead" rules={[{ required: true }]}>
              <Select showSearch optionFilterProp="label"
                options={leads.map((l) => ({ value: l.id, label: `${l.firstName} ${l.lastName || ''} (${l.email || l.phone})` }))}
              />
            </Form.Item>
            <Form.Item name="propertyId" label="Propiedad">
              <Select allowClear showSearch optionFilterProp="label"
                options={properties.map((p) => ({ value: p.id, label: `${p.title} — €${p.price?.toLocaleString()}` }))}
              />
            </Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="value" label="Valor (€)"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
            <Form.Item name="probability" label="Probabilidad (%)"><InputNumber min={0} max={100} style={{ width: '100%' }} /></Form.Item>
          </div>
          {editing && (
            <Form.Item name="stage" label="Etapa">
              <Select options={Object.entries(stageLabels).map(([k, v]) => ({ value: k, label: v }))} />
            </Form.Item>
          )}
          <Form.Item name="notes" label="Notas"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
